import type {
  DestinationTunnelInspectionComplete,
  DestinationTunnelInspectionEvent,
} from "@limrun/api/destination-tunnel-inspection";

export type NetworkNameValue = {
  name: string;
  value: string;
};

export type NetworkHarEntry = DestinationTunnelInspectionComplete & {
  _limrun: DestinationTunnelInspectionComplete["_limrun"] & {
    requestBodyEncoding?: "base64";
  };
};

export type NetworkBodyPreview = {
  text: string;
  encoding: "text" | "base64";
  byteLength: number;
  truncated: boolean;
};

export type NetworkRequestRecord = {
  id: string;
  entry: NetworkHarEntry;
  requestBody?: NetworkBodyPreview;
  responseBody?: NetworkBodyPreview;
};

export type NetworkInspectionEvent = DestinationTunnelInspectionEvent;

export type NetworkInspectionSnapshot = {
  requests: NetworkRequestRecord[];
  warnings: string[];
};

export const MAX_LIVE_NETWORK_ENTRIES = 250;
export const MAX_LIVE_BODY_PREVIEW_BYTES = 64 * 1024;
export const MAX_HAR_ENTRIES = 10_000;

type PendingBodies = {
  request: Uint8Array[];
  requestBytes: number;
  requestTruncated: boolean;
  response: Uint8Array[];
  responseBytes: number;
  responseTruncated: boolean;
};

export class NetworkInspectionAccumulator {
  private readonly pending = new Map<string, PendingBodies>();
  private readonly requests: NetworkRequestRecord[] = [];
  private readonly warnings: string[] = [];
  private lastSequence = 0;
  private listTruncationReported = false;

  constructor(
    private readonly maxEntries = MAX_LIVE_NETWORK_ENTRIES,
    private readonly maxBodyBytes = MAX_LIVE_BODY_PREVIEW_BYTES,
  ) {
    if (!Number.isInteger(maxEntries) || maxEntries < 1) {
      throw new Error("maxEntries must be a positive integer");
    }
    if (!Number.isInteger(maxBodyBytes) || maxBodyBytes < 1) {
      throw new Error("maxBodyBytes must be a positive integer");
    }
  }

  consume(event: NetworkInspectionEvent): boolean {
    if (event.sequence <= this.lastSequence) return false;
    if (event.type !== "gap" && event.sequence > this.lastSequence + 1) {
      this.reportGap(this.lastSequence + 1, event.sequence - 1);
    }
    this.lastSequence = event.sequence;

    switch (event.type) {
      case "gap":
        this.pending.clear();
        this.addWarning(event.data.message);
        return true;
      case "inspection_error":
        this.addWarning(event.data.message);
        return true;
      case "body":
        this.appendBody(event);
        return false;
      case "complete":
        this.complete(event);
        return true;
    }
  }

  addWarning(message: string): void {
    if (!message || this.warnings.includes(message)) return;
    this.warnings.push(message);
    if (this.warnings.length > 20) this.warnings.shift();
  }

  snapshot(): NetworkInspectionSnapshot {
    return {
      requests: this.requests.slice(),
      warnings: this.warnings.slice(),
    };
  }

  private appendBody(
    event: Extract<NetworkInspectionEvent, { type: "body" }>,
  ): void {
    let state = this.pending.get(event.requestId);
    if (!state) {
      if (this.pending.size >= this.maxEntries * 2) {
        this.pending.clear();
        this.addWarning(
          "Pending live requests exceeded the UI limit; incomplete requests were discarded.",
        );
      }
      state = {
        request: [],
        requestBytes: 0,
        requestTruncated: false,
        response: [],
        responseBytes: 0,
        responseTruncated: false,
      };
      this.pending.set(event.requestId, state);
    }
    const response = event.direction === "response";
    const bytes = response ? state.responseBytes : state.requestBytes;
    const take = Math.min(
      event.body.byteLength,
      Math.max(0, this.maxBodyBytes - bytes),
    );
    if (take > 0) {
      (response ? state.response : state.request).push(
        event.body.slice(0, take),
      );
    }
    if (response) {
      state.responseBytes += take;
      state.responseTruncated ||= take < event.body.byteLength;
    } else {
      state.requestBytes += take;
      state.requestTruncated ||= take < event.body.byteLength;
    }
  }

  private complete(
    event: Extract<NetworkInspectionEvent, { type: "complete" }>,
  ): void {
    const state = this.pending.get(event.requestId);
    this.pending.delete(event.requestId);
    this.requests.push({
      id: event.requestId,
      entry: event.data as NetworkHarEntry,
      ...(state?.requestBytes
        ? {
            requestBody: previewFromBytes(
              concatenate(state.request, state.requestBytes),
              event.data.request.postData?.mimeType ??
                headerValue(event.data.request.headers, "content-type"),
              state.requestTruncated ||
                event.data._limrun.requestBodyTruncated === true,
            ),
          }
        : {}),
      ...(state?.responseBytes
        ? {
            responseBody: previewFromBytes(
              concatenate(state.response, state.responseBytes),
              event.data.response.content.mimeType,
              state.responseTruncated ||
                event.data._limrun.responseBodyTruncated === true,
            ),
          }
        : {}),
    });
    if (this.requests.length > this.maxEntries) {
      this.requests.splice(0, this.requests.length - this.maxEntries);
      if (!this.listTruncationReported) {
        this.listTruncationReported = true;
        this.addWarning(
          `Live request history is limited to the newest ${this.maxEntries} entries.`,
        );
      }
    }
  }

  private reportGap(fromSequence: number, toSequence: number): void {
    this.pending.clear();
    this.addWarning(
      `Inspection stream gap: missing sequences ${fromSequence}-${toSequence}. Some requests or body chunks may be incomplete.`,
    );
  }
}

export function decodeNetworkInspectionEvent(
  lastEventId: string,
  text: string,
): NetworkInspectionEvent {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error("inspection event must be valid JSON");
  }
  const envelope = record(value, "inspection event");
  const sequence = safeInteger(envelope.sequence, "inspection sequence");
  if (lastEventId !== String(sequence)) {
    throw new Error("inspection SSE event ID does not match envelope sequence");
  }
  const type = stringValue(envelope.type, "inspection event type");
  if (type === "body") {
    const data = record(envelope.data, "inspection body");
    const direction = data.direction;
    if (direction !== "request" && direction !== "response") {
      throw new Error("inspection body direction must be request or response");
    }
    if (data.encoding !== "base64") {
      throw new Error("inspection body encoding must be base64");
    }
    return {
      type,
      sequence,
      requestId: nonEmptyString(envelope.requestId, "requestId"),
      direction,
      body: decodeBase64(stringValue(data.chunk, "inspection body chunk")),
    };
  }
  if (type === "complete") {
    return {
      type,
      sequence,
      requestId: nonEmptyString(envelope.requestId, "requestId"),
      data: decodeHarEntry(envelope.data),
    };
  }
  const data = record(envelope.data, "inspection event data");
  if (type === "gap") {
    const fromSequence = safeInteger(data.fromSequence, "gap fromSequence");
    const toSequence = safeInteger(data.toSequence, "gap toSequence");
    return {
      type,
      sequence,
      data: {
        fromSequence,
        toSequence,
        message: `Inspection stream gap: missing sequences ${fromSequence}-${toSequence}`,
      },
    };
  }
  if (type === "inspection_error") {
    return {
      type,
      sequence,
      ...(envelope.requestId === undefined
        ? {}
        : { requestId: nonEmptyString(envelope.requestId, "requestId") }),
      data: { ...data, message: stringValue(data.message, "message") },
    };
  }
  throw new Error(`unknown inspection event type ${type}`);
}

// buildNetworkHar wraps live-captured entries in a HAR 1.2 envelope, the
// inverse of parseNetworkHar, so the live view can be downloaded in the same
// format the runtime persists. Live body previews arrive outside the entries,
// so they are folded back in where a HAR carries them: request bodies into
// postData, response bodies into content, base64 markers into the fields
// requestRecord reads them from.
export function buildNetworkHar(requests: NetworkRequestRecord[]): string {
  return JSON.stringify(
    {
      log: {
        version: "1.2",
        creator: { name: "limrun-console", version: "1" },
        entries: requests.map(entryWithBodies),
      },
    },
    null,
    2,
  );
}

function entryWithBodies({
  entry,
  requestBody,
  responseBody,
}: NetworkRequestRecord): NetworkHarEntry {
  if (!requestBody && !responseBody) return entry;
  return {
    ...entry,
    request: requestBody
      ? {
          ...entry.request,
          postData: {
            mimeType:
              entry.request.postData?.mimeType ??
              headerValue(entry.request.headers, "content-type") ??
              "",
            text: requestBody.text,
          },
        }
      : entry.request,
    response: responseBody
      ? {
          ...entry.response,
          content: {
            ...entry.response.content,
            text: responseBody.text,
            ...(responseBody.encoding === "base64"
              ? { encoding: "base64" }
              : {}),
          },
        }
      : entry.response,
    _limrun: {
      ...entry._limrun,
      ...(requestBody?.encoding === "base64"
        ? { requestBodyEncoding: "base64" as const }
        : {}),
      ...(requestBody?.truncated ? { requestBodyTruncated: true } : {}),
      ...(responseBody?.truncated ? { responseBodyTruncated: true } : {}),
    },
  };
}

export function parseNetworkHar(text: string): NetworkInspectionSnapshot {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new Error("network capture is not valid JSON");
  }
  const root = record(value, "HAR");
  const log = record(root.log, "HAR log");
  if (!Array.isArray(log.entries))
    throw new Error("HAR log entries must be an array");
  if (log.entries.length > MAX_HAR_ENTRIES) {
    throw new Error(`HAR contains more than ${MAX_HAR_ENTRIES} entries`);
  }
  const requests = log.entries.map((item, index) =>
    requestRecord(`har-${index}`, decodeHarEntry(item)),
  );

  const warnings: string[] = [];
  const metadata =
    typeof log._limrun === "object" && log._limrun !== null
      ? record(log._limrun, "HAR metadata")
      : undefined;
  if (metadata && Array.isArray(metadata.gaps)) {
    for (const item of metadata.gaps) {
      const gap = record(item, "HAR gap");
      const from = safeInteger(gap.fromSequence, "gap fromSequence");
      const to = safeInteger(gap.toSequence, "gap toSequence");
      warnings.push(
        typeof gap.message === "string"
          ? gap.message
          : `Inspection stream gap: missing sequences ${from}-${to}.`,
      );
    }
  }
  return { requests, warnings };
}

function requestRecord(
  id: string,
  entry: NetworkHarEntry,
): NetworkRequestRecord {
  const requestText = entry.request.postData?.text;
  const responseText = entry.response.content.text;
  return {
    id,
    entry,
    ...(requestText === undefined
      ? {}
      : {
          requestBody: previewFromHarText(
            requestText,
            entry._limrun.requestBodyEncoding,
            entry.request.postData?.mimeType,
            entry._limrun.requestBodyTruncated === true,
          ),
        }),
    ...(responseText === undefined
      ? {}
      : {
          responseBody: previewFromHarText(
            responseText,
            entry.response.content.encoding,
            entry.response.content.mimeType,
            entry._limrun.responseBodyTruncated === true,
          ),
        }),
  };
}

export function formatEscapedText(text: string): string {
  return JSON.stringify(text).slice(1, -1);
}

function decodeHarEntry(value: unknown): NetworkHarEntry {
  const entry = record(value, "HAR entry");
  const request = record(entry.request, "HAR request");
  const response = record(entry.response, "HAR response");
  const content = record(response.content, "HAR response content");
  const extension = record(entry._limrun, "HAR _limrun extension");
  const timings = record(entry.timings, "HAR timings");
  const decodedTimings: Record<string, number> = {};
  for (const [name, duration] of Object.entries(timings)) {
    if (typeof duration === "number" && Number.isFinite(duration)) {
      decodedTimings[name] = duration;
    }
  }
  return {
    startedDateTime: nonEmptyString(entry.startedDateTime, "startedDateTime"),
    time: finiteNumber(entry.time, "time"),
    request: {
      method: nonEmptyString(request.method, "request method"),
      url: nonEmptyString(request.url, "request URL"),
      httpVersion: stringValue(request.httpVersion, "request HTTP version"),
      headers: nameValues(request.headers, "request headers"),
      queryString: nameValues(request.queryString, "request query string"),
      cookies: arrayValue(
        request.cookies,
        "request cookies",
      ) as NetworkHarEntry["request"]["cookies"],
      headersSize: finiteNumber(request.headersSize, "request headersSize"),
      bodySize: finiteNumber(request.bodySize, "request bodySize"),
      ...(request.postData === undefined
        ? {}
        : { postData: decodePostData(request.postData) }),
    },
    response: {
      status: finiteNumber(response.status, "response status"),
      statusText: stringValue(response.statusText, "response statusText"),
      httpVersion: stringValue(response.httpVersion, "response HTTP version"),
      headers: nameValues(response.headers, "response headers"),
      cookies: arrayValue(
        response.cookies,
        "response cookies",
      ) as NetworkHarEntry["response"]["cookies"],
      content: {
        size: finiteNumber(content.size, "response content size"),
        mimeType: stringValue(content.mimeType, "response content mimeType"),
        ...(content.text === undefined
          ? {}
          : { text: stringValue(content.text, "response content text") }),
        ...(content.encoding === undefined
          ? {}
          : {
              encoding: stringValue(
                content.encoding,
                "response content encoding",
              ),
            }),
      },
      redirectURL: stringValue(response.redirectURL, "response redirectURL"),
      headersSize: finiteNumber(response.headersSize, "response headersSize"),
      bodySize: finiteNumber(response.bodySize, "response bodySize"),
    },
    cache:
      typeof entry.cache === "object" && entry.cache !== null
        ? record(entry.cache, "HAR cache")
        : {},
    timings: decodedTimings as unknown as NetworkHarEntry["timings"],
    ...(typeof entry.serverIPAddress === "string"
      ? { serverIPAddress: entry.serverIPAddress }
      : {}),
    ...(typeof entry.connection === "string"
      ? { connection: entry.connection }
      : {}),
    _limrun: {
      tunnelId: stringValue(extension.tunnelId, "tunnelId"),
      selectorId: stringValue(extension.selectorId, "selectorId"),
      ...(typeof extension.error === "string"
        ? { error: extension.error }
        : {}),
      ...(extension.requestBodyEncoding === "base64"
        ? { requestBodyEncoding: "base64" as const }
        : {}),
      ...(extension.requestBodyTruncated === true
        ? { requestBodyTruncated: true }
        : {}),
      ...(extension.responseBodyTruncated === true
        ? { responseBodyTruncated: true }
        : {}),
      ...(extension.responseTrailers === undefined
        ? {}
        : {
            responseTrailers: nameValues(
              extension.responseTrailers,
              "response trailers",
            ),
          }),
    },
  };
}

function decodePostData(value: unknown): { mimeType: string; text: string } {
  const postData = record(value, "request postData");
  return {
    mimeType: stringValue(postData.mimeType, "request postData mimeType"),
    text: stringValue(postData.text, "request postData text"),
  };
}

function previewFromBytes(
  bytes: Uint8Array,
  contentType: string | undefined,
  truncated: boolean,
): NetworkBodyPreview {
  return {
    text: isTextualContentType(contentType)
      ? new TextDecoder().decode(bytes)
      : bytesToBase64(bytes),
    encoding: isTextualContentType(contentType) ? "text" : "base64",
    byteLength: bytes.byteLength,
    truncated,
  };
}

function previewFromHarText(
  text: string,
  encoding: string | undefined,
  contentType: string | undefined,
  truncated: boolean,
): NetworkBodyPreview {
  if (encoding === "base64") {
    const bytes = decodeBase64(text);
    if (isTextualContentType(contentType)) {
      return {
        text: new TextDecoder().decode(bytes),
        encoding: "text",
        byteLength: bytes.byteLength,
        truncated,
      };
    }
    return {
      text,
      encoding: "base64",
      byteLength: bytes.byteLength,
      truncated,
    };
  }
  return {
    text,
    encoding: "text",
    byteLength: new TextEncoder().encode(text).byteLength,
    truncated,
  };
}

function isTextualContentType(contentType: string | undefined): boolean {
  const mimeType = contentType?.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return (
    mimeType.startsWith("text/") ||
    mimeType.endsWith("+json") ||
    mimeType.endsWith("+xml") ||
    mimeType === "application/json" ||
    mimeType === "application/xml" ||
    mimeType === "application/javascript" ||
    mimeType === "application/x-javascript" ||
    mimeType === "application/x-www-form-urlencoded" ||
    mimeType === "application/graphql"
  );
}

function headerValue(
  headers: Array<{ name: string; value: string }>,
  name: string,
): string | undefined {
  return headers.find((header) => header.name.toLowerCase() === name)?.value;
}

function concatenate(chunks: Uint8Array[], total: number): Uint8Array {
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

function decodeBase64(value: string): Uint8Array {
  let binary: string;
  try {
    if (value.length % 4 !== 0) throw new Error("invalid base64 length");
    binary = atob(value);
    if (btoa(binary) !== value) throw new Error("non-canonical base64");
  } catch {
    throw new Error("inspection body chunk must be valid base64");
  }
  const result = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    result[index] = binary.charCodeAt(index);
  }
  return result;
}

function record(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${name} must be an object`);
  }
  return value as Record<string, unknown>;
}

function arrayValue(value: unknown, name: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${name} must be an array`);
  return value;
}

function nameValues(value: unknown, name: string): NetworkNameValue[] {
  return arrayValue(value, name).map((item) => {
    const pair = record(item, name);
    return {
      name: stringValue(pair.name, `${name} name`),
      value: stringValue(pair.value, `${name} value`),
    };
  });
}

function stringValue(value: unknown, name: string): string {
  if (typeof value !== "string") throw new Error(`${name} must be a string`);
  return value;
}

function nonEmptyString(value: unknown, name: string): string {
  const result = stringValue(value, name);
  if (!result) throw new Error(`${name} must not be empty`);
  return result;
}

function finiteNumber(value: unknown, name: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${name} must be a finite number`);
  }
  return value;
}

function safeInteger(value: unknown, name: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new Error(`${name} must be a safe non-negative integer`);
  }
  return value as number;
}
